import { Button, toast } from "@devhop/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import {
  BanIcon,
  DownloadIcon,
  Loader2Icon,
  PencilIcon,
  PrinterIcon,
} from "lucide-react";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { EditPackageSelectedClassesDialog } from "@/modules/students/presentation/ui/EditPackageSelectedClassesDialog";
import { formatDate, formatTime } from "@/shared/lib/date-time";
import { RECEIPT_THERMAL_WIDTH_MM } from "@/shared/lib/receipt-print-48mm";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { receiptKeys, useReceiptQuery } from "../api/queries";
import { EditReceiptAssistItemsDialog } from "../ui/EditReceiptAssistItemsDialog";
import {
  groupRoomBookingReceiptItems,
  hasPricedAssistReceiptItems,
  RoomBookingReceiptGroupCard,
} from "../ui/RoomBookingReceiptGroupCard";
import {
  downloadReceiptDetailAsImage,
  ReceiptPreviewByApiDialog,
} from "../ui/ReceiptPreviewByApiDialog";
import { VoidReceiptDialog } from "../ui/VoidReceiptDialog";

export function ReceiptDetailPage() {
  const { id } = useParams({ from: "/app/billing/receipts/$id" });
  const qc = useQueryClient();
  const canVoid = useActionPermission("billing:void");
  const canAmendItems = useActionPermission(["billing:create"]);
  const { data: receipt, isLoading } = useReceiptQuery(id ?? null);
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [isVoidDialogOpen, setIsVoidDialogOpen] = useState(false);
  const [isEditItemsOpen, setIsEditItemsOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [editPackageItemId, setEditPackageItemId] = useState<string | null>(
    null,
  );

  const isVoided = receipt?.status === "voided";
  const hasRoomBookingItems =
    receipt?.items.some((item) => item.itemType === "room_booking") ?? false;

  const firstStudentItem = receipt?.items.find(
    (i) => i.studentName != null || i.memberId != null,
  );
  const editingPackageItem = receipt?.items.find(
    (i) => i.id === editPackageItemId,
  );
  const payment = receipt?.payment;
  const hasDiscount =
    (payment?.discountAmount ?? 0) > 0 &&
    (payment?.originalAmount ?? payment?.amount ?? 0) > (payment?.amount ?? 0);

  const handleDownloadImage = async () => {
    if (!receipt || isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadReceiptDetailAsImage(receipt);
      toast.success("ດາວໂຫຼດຮູບໃບເກັບເງິນແລ້ວ");
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "ດາວໂຫຼດຮູບບໍ່ສຳເລັດ",
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ໃບເກັບເງິນ", to: "/app/billing/receipts" },
            { label: "ລາຍລະອຽດ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-bold text-2xl">ໃບເກັບເງິນ</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              {receipt ? receipt.receiptNo : "ກຳລັງໂຫຼດ..."}
            </p>
          </div>
          {receipt && (
            <div className="flex gap-2">
              {!isVoided && canAmendItems && hasRoomBookingItems && id && (
                <Button
                  variant="outline"
                  onClick={() => setIsEditItemsOpen(true)}
                >
                  <PencilIcon className="mr-2 size-4" />
                  ແກ້ໄຂລາຍການ
                </Button>
              )}
              {!isVoided && canVoid && (
                <Button
                  variant="destructive"
                  onClick={() => setIsVoidDialogOpen(true)}
                >
                  <BanIcon className="mr-2 size-4" />
                  ຍົກເລີກໃບເກັບເງິນ
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => void handleDownloadImage()}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <Loader2Icon className="mr-2 size-4 animate-spin" />
                ) : (
                  <DownloadIcon className="mr-2 size-4" />
                )}
                ດາວໂຫຼດຮູບ ({RECEIPT_THERMAL_WIDTH_MM}mm)
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsPrintDialogOpen(true)}
              >
                <PrinterIcon className="mr-2 size-4" />
                ພິມໃບເກັບເງິນ
              </Button>
            </div>
          )}
        </div>

        {isLoading && !receipt ? (
          <div className="flex items-center justify-center py-24">
            <Loader2Icon className="size-8 animate-spin text-muted-foreground" />
          </div>
        ) : receipt ? (
          <div className="rounded-xl border bg-card p-6">
            {isVoided && (
              <div className="mb-6 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
                <p className="font-semibold text-destructive">
                  ໃບເກັບເງິນນີ້ຖືກຍົກເລີກແລ້ວ
                </p>
                {receipt.voidedAt && (
                  <p className="mt-1 text-muted-foreground text-sm">
                    ວັນທີ: {formatDate(receipt.voidedAt)}{" "}
                    {new Date(receipt.voidedAt).toLocaleTimeString("lo-LA", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                    {receipt.voidedByName ? ` · ໂດຍ ${receipt.voidedByName}` : ""}
                  </p>
                )}
                {receipt.voidReason && (
                  <p className="mt-2 text-sm">
                    <span className="text-muted-foreground">ເຫດຜົນ: </span>
                    {receipt.voidReason}
                  </p>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                  ເລກທີ
                </p>
                <p className="font-mono font-semibold">{receipt.receiptNo}</p>
              </div>
              <div>
                <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                  ວັນທີອອກ
                </p>
                <p className="font-semibold">
                  {formatDate(receipt.issuedAt)}{" "}
                  {new Date(receipt.issuedAt).toLocaleTimeString("lo-LA", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              </div>
              <div>
                <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                  ຜູ້ອອກ
                </p>
                <p className="font-semibold">{receipt.issuedByName ?? "-"}</p>
              </div>
              <div>
                <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                  ຈຳນວນເງິນທັງໝົດ
                </p>
                {hasDiscount ? (
                  <div className="space-y-1">
                    <p className="text-muted-foreground text-sm line-through">
                      {(payment?.originalAmount ?? 0).toLocaleString("lo-LA")}{" "}
                      ກີບ
                    </p>
                    <p className="text-muted-foreground text-sm">
                      ສ່ວນຫຼຸດ: -
                      {(payment?.discountAmount ?? 0).toLocaleString("lo-LA")}{" "}
                      ກີບ
                      {payment?.discountType === "percentage" &&
                        payment?.discountValue != null &&
                        ` (${payment.discountValue}%)`}
                    </p>
                    <p className="font-bold text-red-600 text-xl">
                      {receipt.payment.amount.toLocaleString("lo-LA")} ກີບ
                    </p>
                  </div>
                ) : (
                  <p className="font-bold text-red-600 text-xl">
                    {receipt.payment.amount.toLocaleString("lo-LA")} ກີບ
                  </p>
                )}
              </div>
            </div>

            {firstStudentItem &&
              (firstStudentItem.studentName || firstStudentItem.memberId) && (
                <div className="mt-8 border-t pt-6">
                  <h3 className="mb-4 font-semibold text-lg">ຂໍ້ມູນນັກຮຽນ</h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {firstStudentItem.studentName && (
                      <div>
                        <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                          ຊື່
                        </p>
                        <p className="font-semibold">
                          {firstStudentItem.studentName}
                        </p>
                      </div>
                    )}
                    {firstStudentItem.memberId && (
                      <div>
                        <p className="mb-1 font-medium text-muted-foreground text-xs uppercase">
                          ເລກສະມາຊິກ
                        </p>
                        <p className="font-semibold">
                          {firstStudentItem.memberId}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

            <div className="mt-8 border-t pt-6">
              <h3 className="mb-4 font-semibold text-lg">ລາຍການ</h3>
              <div className="space-y-4">
                {(() => {
                  if (!receipt) return null;
                  const { roomBookingGroups, orphanAssistItems, otherItems } =
                    groupRoomBookingReceiptItems(receipt.items);
                  const showLegacyAssist = !hasPricedAssistReceiptItems(
                    receipt.items,
                  );

                  return (
                    <>
                      {roomBookingGroups.map((group) => (
                        <RoomBookingReceiptGroupCard
                          key={group.roomItem.id}
                          group={group}
                          showLegacyAssistNames={showLegacyAssist}
                        />
                      ))}

                      {orphanAssistItems.map((item) => (
                        <div
                          key={item.id}
                          className="flex justify-between rounded-lg border p-4"
                        >
                          <div className="flex-1">
                            <p className="font-medium">{item.description}</p>
                            <p className="mt-2 text-muted-foreground text-sm">
                              {item.quantity} ×{" "}
                              {item.unitPrice.toLocaleString("lo-LA")} ກີບ
                            </p>
                          </div>
                          <p className="ml-4 font-semibold">
                            {item.totalPrice.toLocaleString("lo-LA")} ກີບ
                          </p>
                        </div>
                      ))}

                      {otherItems.map((item) => (
                        <div
                          key={item.id}
                          className="flex justify-between rounded-lg border p-4"
                        >
                          <div className="flex-1">
                            <p className="whitespace-pre-line font-medium">
                              {item.description}
                            </p>
                            <div className="mt-2 space-y-1 text-muted-foreground text-sm">
                              {item.startDate && item.expireDate && (
                                <p>
                                  ຊ່ວງເວລາທີ່ໃຊ້ໄດ້: {formatDate(item.startDate)}{" "}
                                  – {formatDate(item.expireDate)}
                                </p>
                              )}
                              {item.totalHours != null &&
                                item.pricePerHour != null && (
                                  <p>
                                    {item.totalHours} ຊົ່ວໂມງ ×{" "}
                                    {item.pricePerHour.toLocaleString("lo-LA")}{" "}
                                    ກີບ/ຊົ່ວໂມງ
                                  </p>
                                )}
                              <p>
                                ຈຳນວນ: {item.quantity} ×{" "}
                                {item.unitPrice.toLocaleString("lo-LA")} ກີບ
                              </p>
                              {item.itemType === "package_purchase" &&
                                item.selectedClasses &&
                                item.selectedClasses.length > 0 && (
                                  <div className="mt-2 border-t pt-2">
                                    <p className="font-medium text-foreground">
                                      ຄລາດສ໌ທີ່ເລືອກ
                                    </p>
                                    <ul className="mt-1 list-inside list-disc space-y-0.5">
                                      {item.selectedClasses.map((s) => (
                                        <li
                                          key={
                                            s.allowedRuleId ??
                                            `${s.classTypeName}-${s.levelName}`
                                          }
                                        >
                                          {s.classTypeName} - {s.levelName}
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              {item.itemType === "package_purchase" &&
                                item.studentPackageId &&
                                item.studentId &&
                                item.packageTemplateId &&
                                (item.selectedClasses?.length ?? 0) > 0 &&
                                !isVoided && (
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="mt-3"
                                    onClick={() =>
                                      setEditPackageItemId(item.id)
                                    }
                                  >
                                    ແກ້ໄຂຄລາດສ໌ທີ່ເລືອກ
                                  </Button>
                                )}
                            </div>
                          </div>
                          <p className="ml-4 font-semibold">
                            {item.totalPrice.toLocaleString("lo-LA")} ກີບ
                          </p>
                        </div>
                      ))}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        ) : (
          <p className="py-12 text-center text-muted-foreground">ບໍ່ພົບໃບເກັບເງິນ</p>
        )}
      </Main>

      {editPackageItemId != null &&
        editingPackageItem != null &&
        editingPackageItem.studentId != null &&
        editingPackageItem.studentPackageId != null &&
        editingPackageItem.packageTemplateId != null && (
          <EditPackageSelectedClassesDialog
            open
            onOpenChange={(o) => {
              if (!o) setEditPackageItemId(null);
            }}
            studentId={editingPackageItem.studentId}
            packageId={editingPackageItem.studentPackageId}
            templateId={editingPackageItem.packageTemplateId}
            initialSelectedRuleIds={(
              editingPackageItem.selectedClasses?.map((s) => s.allowedRuleId) ??
              []
            ).filter((x): x is string => !!x)}
            initialClassSessions={
              editingPackageItem.selectedClasses
                ?.filter((s) => s.allowedRuleId)
                .map((s) => ({
                  ruleId: s.allowedRuleId as string,
                  sessions: s.allocatedSessions ?? 0,
                })) ?? []
            }
            initialSelectedClasses={
              editingPackageItem.selectedClasses
                ?.filter((s) => s.allowedRuleId)
                .map((s) => ({
                  allowedRuleId: s.allowedRuleId as string,
                  classTypeName: s.classTypeName,
                  levelName: s.levelName,
                  allocatedSessions: s.allocatedSessions ?? 0,
                  remainingSessions: s.remainingSessions ?? 0,
                })) ?? []
            }
            onSuccess={() => {
              void qc.invalidateQueries({
                queryKey: receiptKeys.detail(id ?? ""),
              });
            }}
          />
        )}

      <ReceiptPreviewByApiDialog
        open={isPrintDialogOpen}
        onOpenChange={setIsPrintDialogOpen}
        receiptId={id ?? null}
      />

      {receipt && id && (
        <VoidReceiptDialog
          receiptId={id}
          receiptNo={receipt.receiptNo}
          open={isVoidDialogOpen}
          onOpenChange={setIsVoidDialogOpen}
          onSuccess={() => {
            void qc.invalidateQueries({ queryKey: receiptKeys.detail(id) });
          }}
        />
      )}

      {id && (
        <EditReceiptAssistItemsDialog
          open={isEditItemsOpen}
          onOpenChange={setIsEditItemsOpen}
          receiptId={id}
          onSuccess={() => {
            void qc.invalidateQueries({ queryKey: receiptKeys.detail(id) });
          }}
        />
      )}
    </>
  );
}
